// @ts-nocheck
/**
 * Music Identifier Module
 * Captura áudio da rádio, gera assinatura Shazam no browser (shazam.js)
 * e identifica via /ajax/identify-music.php (discovery server-side).
 */

class MusicIdentifier {
    constructor(options = {}) {
        this.recordDuration = options.recordDuration || 8000;
        this.identifyEndpoint = options.endpoint || '/ajax/identify-music.php';
        this.shazamModuleUrl = options.shazamModuleUrl || MusicIdentifier.resolveShazamModuleUrl();
        this.radioId = options.radioId || 0;
        this.onSuccess = options.onSuccess || (() => {});
        this.onError = options.onError || (() => {});
        this.activeRecording = null;
        this._shazamModule = null;
    }

    static resolveShazamModuleUrl() {
        if (window.RPRO_SHAZAM_MODULE_URL) {
            return String(window.RPRO_SHAZAM_MODULE_URL);
        }
        var scripts = document.querySelectorAll('script[src*="music-identifier.js"]');
        for (var i = scripts.length - 1; i >= 0; i--) {
            var src = scripts[i].getAttribute('src') || '';
            if (src.indexOf('music-identifier.js') !== -1) {
                return src.replace(/music-identifier\.js(\?.*)?$/i, 'shazam.js');
            }
        }
        return '/assets/js/shazam.js';
    }

    setRadioId(radioId) {
        this.radioId = parseInt(radioId, 10) || 0;
    }

    async loadShazamModule() {
        if (this._shazamModule) {
            return this._shazamModule;
        }
        this._shazamModule = import('./shazam');
        return this._shazamModule;
    }

    async identifyCurrentTrack(audioElement) {
        try {
            if (!audioElement) {
                throw new Error('Elemento de áudio não fornecido');
            }

            await this.delay(1000);
            const audioData = await this.captureAudioFromElement(audioElement);

            if (!audioData || audioData.byteLength === 0) {
                throw new Error('Falha ao capturar áudio');
            }

            const result = await this.sendForIdentification(audioData);
            if (result) {
                this.onSuccess(result);
                return result;
            }

            this.onError(new Error('Música não identificada'));
            return null;
        } catch (error) {
            this.onError(error);
            return null;
        }
    }

    captureAudioFromElement(audioElement) {
        return new Promise((resolve, reject) => {
            try {
                if (this.activeRecording) {
                    reject(new Error('Já existe uma gravação ativa'));
                    return;
                }

                const streamUrl = audioElement.src;
                if (!streamUrl) {
                    reject(new Error('Stream URL não disponível'));
                    return;
                }

                const abortController = new AbortController();
                this.activeRecording = abortController;

                fetch('/ajax/capture-stream.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'same-origin',
                    body: JSON.stringify({
                        streamUrl: streamUrl,
                        radioId: this.radioId || 0,
                        duration: Math.floor(this.recordDuration / 1000)
                    }),
                    signal: abortController.signal
                })
                    .then(response => {
                        if (!response.ok) {
                            throw new Error('Backend retornou ' + response.status);
                        }
                        return response.arrayBuffer();
                    })
                    .then(arrayBuffer => {
                        this.activeRecording = null;
                        resolve(arrayBuffer);
                    })
                    .catch(error => {
                        if (error.name === 'AbortError') {
                            reject(new Error('Captura cancelada'));
                        } else {
                            reject(error);
                        }
                        this.activeRecording = null;
                    });
            } catch (error) {
                this.activeRecording = null;
                reject(error);
            }
        });
    }

    cancelRecording() {
        if (this.activeRecording) {
            this.activeRecording.abort();
            this.activeRecording = null;
        }
    }

    /**
     * Infere MIME a partir dos magic bytes do stream Icecast/Shoutcast.
     */
    detectCaptureMimeType(arrayBuffer) {
        if (!(arrayBuffer instanceof ArrayBuffer) || arrayBuffer.byteLength < 4) {
            return { mime: 'audio/mpeg', ext: 'mp3' };
        }
        const bytes = new Uint8Array(arrayBuffer, 0, Math.min(12, arrayBuffer.byteLength));

        if (bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) {
            return { mime: 'audio/webm', ext: 'webm' };
        }
        if (bytes[0] === 0x4f && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
            return { mime: 'audio/ogg', ext: 'ogg' };
        }
        if (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) {
            return { mime: 'audio/mpeg', ext: 'mp3' };
        }
        if (bytes[0] === 0xff && (bytes[1] === 0xfb || bytes[1] === 0xf3 || bytes[1] === 0xfa || bytes[1] === 0xf2)) {
            return { mime: 'audio/mpeg', ext: 'mp3' };
        }
        if (bytes[0] === 0xff && (bytes[1] === 0xf1 || bytes[1] === 0xf9)) {
            return { mime: 'audio/aac', ext: 'aac' };
        }
        if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) {
            return { mime: 'audio/wav', ext: 'wav' };
        }

        return { mime: 'audio/mpeg', ext: 'mp3' };
    }

    async buildSignatureFromCapture(arrayBuffer) {
        const shazam = await this.loadShazamModule();
        const detected = this.detectCaptureMimeType(arrayBuffer);
        const blob = new Blob([arrayBuffer], { type: detected.mime });

        let pcm;
        try {
            pcm = await shazam.decodeAudio(blob);
        } catch (decodeErr) {
            if (detected.ext !== 'mp3') {
                const mp3Blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
                pcm = await shazam.decodeAudio(mp3Blob);
            } else {
                throw decodeErr;
            }
        }

        const generated = shazam.generateSignature(pcm);
        if (!generated || !generated.uri || generated.peakCount < 1) {
            throw new Error('Assinatura Shazam insuficiente (poucos picos)');
        }

        return {
            signature_uri: generated.uri,
            sample_ms: generated.samplems || Math.floor(this.recordDuration)
        };
    }

    async sendSignatureForIdentification(signature) {
        const response = await fetch(this.identifyEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
                signature_uri: signature.signature_uri,
                sample_ms: signature.sample_ms
            })
        });

        let data = null;
        try {
            data = await response.json();
        } catch (_) {
            return { status: 'error' };
        }

        if (response.status === 429 || (data && data.error === 'rate_limited')) {
            return {
                status: 'rate_limited',
                retryAfter: data && data.retryAfter ? data.retryAfter : 25
            };
        }

        if (data && data.success === true) {
            return { status: 'ok', data: data };
        }

        if (data && (data.code === 'not_identified' || data.error === 'Música não identificada')) {
            return { status: 'not_identified' };
        }

        return { status: 'error' };
    }

    async sendLegacyAudioForIdentification(arrayBuffer) {
        const detected = this.detectCaptureMimeType(arrayBuffer);
        const audioBlob = new Blob([arrayBuffer], { type: detected.mime });
        const formData = new FormData();
        formData.append('audio', audioBlob, 'audio.' + detected.ext);
        formData.append('format', detected.ext);

        const response = await fetch(this.identifyEndpoint, {
            method: 'POST',
            credentials: 'same-origin',
            body: formData
        });

        let data = null;
        try {
            data = await response.json();
        } catch (_) {
            return { status: 'error' };
        }

        if (response.status === 429 || (data && data.error === 'rate_limited')) {
            return { status: 'rate_limited' };
        }

        if (data && data.success === true) {
            return { status: 'ok', data: data };
        }

        return { status: 'error' };
    }

    async sendForIdentification(arrayBuffer) {
        try {
            const signature = await this.buildSignatureFromCapture(arrayBuffer);
            const viaSignature = await this.sendSignatureForIdentification(signature);

            if (viaSignature.status === 'ok') {
                return viaSignature.data;
            }

            // Shazam respondeu mas não achou faixa — não reenvia áudio bruto (evita 2º POST + rate limit).
            if (viaSignature.status === 'not_identified' || viaSignature.status === 'rate_limited') {
                return null;
            }
        } catch (sigErr) {
            console.warn('[MusicIdentifier] Assinatura client-side falhou, tentando legacy:', sigErr);
            const legacy = await this.sendLegacyAudioForIdentification(arrayBuffer);
            if (legacy.status === 'ok') {
                return legacy.data;
            }
        }

        return null;
    }

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

window.MusicIdentifier = MusicIdentifier;

export { MusicIdentifier };
export function initMusicIdentifier() {
    if (typeof window !== 'undefined') window.MusicIdentifier = MusicIdentifier;
    return MusicIdentifier;
}
